import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import {
  getScheme,
  getSchemeList,
  getSchemeMasterList,
  SchemeApiOptions,
} from "../api/scheme";
import { SchemeType } from "../types";

export const useSchemes = <T extends SchemeType = "all">(
  options: Omit<SchemeApiOptions, "page"> & {
    enabled?: boolean;
  } = { type: "all" }
) => {
  const { enabled = true, ...restOfOptions } = options;

  const response = useInfiniteQuery({
    queryKey: [
      "funds/list",
      options.ids?.toString(),
      options.type,
      options.isin,
      options.frequency,
      options.search,
      options.amc_id,
      options.product_type,
    ],
    queryFn: (context) => {
      const apiOptions: SchemeApiOptions = {
        page: context.pageParam,
        ...restOfOptions,
      };

      return getSchemeList<T>(apiOptions);
    },
    getNextPageParam: (lastPage) => {
      return lastPage.next
        ? lastPage.next?.match(/page=(\d+)/)?.[1]
        : undefined;
    },
    initialPageParam: "1",
    select: (queryResponse) => queryResponse.pages,
    enabled,
    placeholderData: (prev) => prev,
    staleTime: 60 * 60 * 1000, // 1 hour
  });

  const schemes = useMemo(() => {
    return response.data?.flatMap((page) => page.results);
  }, [response.data]);

  const findById = (id: number) => {
    return id ? schemes?.find((d) => d.id.toString() === id?.toString()) : null;
  };

  return {
    ...response,
    schemes,
    findById,
  };
};

export const useScheme = <T extends "all" | "sip" | "stp" | "swp">(
  schemeId: number,
  type: T
) => {
  return useQuery({
    queryKey: [`schemes/get`, { schemeId, type }],
    queryFn: () => getScheme<T>({ id: schemeId, type }),
    enabled: !!schemeId,
  });
};

export function useSchemeMasterList(
  variables?: { page?: number; pageSize?: number; isin?: string },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [],
    queryFn: () =>
      getSchemeMasterList({
        page: variables?.page || 1,
        pageSize: variables?.pageSize || 10,
        isin: variables?.isin,
      }),
    ...options,
  });
}

export function useSchemeMasterByIsin(isin: string) {
  return useQuery({
    queryKey: [`schemes/master`, { isin }],
    queryFn: () =>
      getSchemeMasterList({
        page: 1,
        pageSize: 1,
        isin,
      }),
    select: (data) => data.results?.[0],
    enabled: !!isin,
  });
}

export function useFetchSchemesByIsin<T extends SchemeType>(type: T) {
  return useCallback(
    async (isins: string[]) => {
      const result = await getSchemeList<T>({
        type,
        isin: isins.join(","),
      });
      return result.results;
    },
    [type]
  );
}
